import React, { useEffect, useState } from 'react';
import { Paper, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingComponent from '../../../../../app/layout/LoadingComponent';
import ListTitleBox from '../../../../../app/components/list/ListTitleBox';
import TimeRangeSelector from '../../../../../app/components/TimeRangeSelector';
import { AvailabilityScheduleDto } from '../../../../../app/data/models/common/availability';
import dayjs, { Dayjs } from 'dayjs';
import { toast } from 'react-toastify';
import api from '../../../../../app/api/api';

export const MenuAvailability: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { restaurantId, menuId } = useParams<{
    restaurantId: string;
    menuId: string;
  }>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availabilities, setAvailabilities] = useState<
    AvailabilityScheduleDto[]
  >([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    [key: number]: boolean;
  }>({});
  const [, setTouchedFields] = useState<{
    [key: number]: { start?: boolean; end?: boolean };
  }>({});

  useEffect(() => {
    loadAvailabilities();
  }, [menuId]);

  const loadAvailabilities = async () => {
    try {
      const response = await api.Menu.getAvailabilities(restaurantId, menuId);

      // Initialize all 7 days
      const allDays: AvailabilityScheduleDto[] = Array.from(
        { length: 7 },
        (_, i) => ({
          dayOfWeek: i,
          isEnabled: false,
          isAllDay: true,
          startTime: null,
          endTime: null,
        })
      );

      // Update with server data if available
      if (response && response.length > 0) {
        response.forEach((schedule: AvailabilityScheduleDto) => {
          const formattedSchedule = {
            ...schedule,
            startTime: schedule.startTime
              ? dayjs(schedule.startTime, 'HH:mm').format('HH:mm')
              : null,
            endTime: schedule.endTime
              ? dayjs(schedule.endTime, 'HH:mm').format('HH:mm')
              : null,
          };
          allDays[schedule.dayOfWeek] = formattedSchedule;
        });
      }

      setAvailabilities(allDays);
    } catch (error) {
      console.error('Error loading availabilities:', error);
      toast.error(t('menu.availability.load-error'));
    } finally {
      setLoading(false);
    }
  };

  const handleTimeBlur = (index: number, field: 'start' | 'end') => {
    setTouchedFields((prev) => ({
      ...prev,
      [index]: { ...prev[index], [field]: true },
    }));
  };

  const handleUpdate = (
    index: number,
    updates: Partial<AvailabilityScheduleDto>
  ) => {
    setAvailabilities((prev) =>
      prev.map((a, i) => (i === index ? { ...a, ...updates } : a))
    );
    setHasChanges(true);
  };

  const handleTimeChange = (
    index: number,
    field: 'start' | 'end',
    value: Dayjs | null
  ) => {
    // If value exists but is invalid, don't update
    if (value && !value.isValid()) return;

    handleUpdate(index, {
      [`${field}Time`]: value?.format('HH:mm') || null,
    });
  };

  const handleSave = async () => {
    if (saving) return;

    // Reset and check validation errors
    setValidationErrors({});
    const newValidationErrors: { [key: number]: boolean } = {};

    availabilities.forEach((schedule, index) => {
      if (
        schedule.isEnabled &&
        !schedule.isAllDay &&
        (schedule.startTime === null || schedule.endTime === null)
      ) {
        newValidationErrors[index] = true;
      }
    });

    if (Object.keys(newValidationErrors).length > 0) {
      setValidationErrors(newValidationErrors);
      toast.error(t('menu.availability.validation-error'));
      return;
    }

    setSaving(true);
    try {
      const formattedAvailabilities = availabilities.map((a) => ({
        ...a,
        startTime:
          a.isEnabled && !a.isAllDay && a.startTime !== null
            ? dayjs(a.startTime, 'HH:mm').format('HH:mm:00')
            : null,
        endTime:
          a.isEnabled && !a.isAllDay && a.endTime !== null
            ? dayjs(a.endTime, 'HH:mm').format('HH:mm:00')
            : null,
      }));
      await api.Menu.updateAvailabilities(restaurantId, menuId, {
        availabilitySchedules: formattedAvailabilities,
      });
      toast.success(t('menu.availability.save-success'));
      setHasChanges(false);
    } catch (error) {
      console.error('Error updating availabilities:', error);
      toast.error(t('menu.availability.save-error'));
    } finally {
      setSaving(false);
    }
  };

  const header = (
    <ListTitleBox
      title={t('menu.availability.title')}
      primaryButtonText={t('common.save')}
      onPrimaryClick={handleSave}
      primaryButtonDisabled={!hasChanges || saving}
      onBackClick={() => navigate(-1)}
    />
  );

  if (loading) {
    return <LoadingComponent header={header} />;
  }

  return (
    <>
      {header}
      <Paper sx={{ p: 3 }}>
        <Stack spacing={3}>
          {availabilities.map((schedule, index) => (
            <TimeRangeSelector
              key={schedule.dayOfWeek}
              day={t(`common.days.${schedule.dayOfWeek}`)}
              isEnabled={schedule.isEnabled}
              isAllDay={schedule.isAllDay}
              startTime={
                schedule.startTime ? dayjs(schedule.startTime, 'HH:mm') : null
              }
              endTime={
                schedule.endTime ? dayjs(schedule.endTime, 'HH:mm') : null
              }
              onEnabledChange={(enabled) =>
                handleUpdate(index, { isEnabled: enabled })
              }
              onAllDayChange={(allDay) =>
                handleUpdate(index, { isAllDay: allDay })
              }
              onStartTimeChange={(value) =>
                handleTimeChange(index, 'start', value)
              }
              onEndTimeChange={(value) => handleTimeChange(index, 'end', value)}
              error={{
                start: validationErrors[index] && !schedule.startTime,
                end: validationErrors[index] && !schedule.endTime,
              }}
              onStartTimeBlur={() => handleTimeBlur(index, 'start')}
              onEndTimeBlur={() => handleTimeBlur(index, 'end')}
            />
          ))}
        </Stack>
      </Paper>
    </>
  );
};
